import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CardMedia,
  IconButton,
  LinearProgress,
} from "@mui/material";
import {
  ExpandMore,
  MenuBook,
  Spellcheck,
  CheckCircle,
  Cancel,
  PlayArrow,
  Pause,
  Replay,
  VolumeUp,
  VolumeOff,
  Headset,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

// ============================================================
// Type Definitions
// ============================================================

interface SubQuestionOption {
  label: string;
  isTrue?: boolean;
  isCorrect?: boolean;
}

interface SubQuestion {
  question: string;
  options: string[] | SubQuestionOption[];
  correctOption?: string;
}

interface MultipleChoiceQuestionData {
  id: string;
  type: string;
  question: string;
  points?: number;
  passage?: string;
  vocabularyWords?: Array<{ word: string; meaning: string }>;
  options?: string[];
  correctAnswer?: number;
  questions?: SubQuestion[];
  subQuestions?: SubQuestion[];
  // Media fields — consistent convention:
  //   asset    = image (.jpg, .png)
  //   audioUrl = audio (.mp3, .m4a, .wav)
  asset?: string;
  audioUrl?: string;
}

interface MultipleChoiceQuestionProps {
  question: MultipleChoiceQuestionData;
  userAnswer?: any;
  selectedAnswer?: number | Record<number, number>;
  onAnswerChange?: (answer: any) => void;
  onComplete?: (isCorrect: boolean, timeSpent: number) => void;
  disabled?: boolean;
  showResult?: boolean;
  reviewMode?: boolean;
}

// ============================================================
// Helpers
// ============================================================

/** Format seconds as mm:ss */
const formatTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Quick guard: only treat audioUrl as audio if it has an audio extension.
 * This catches any leftover junk URLs that haven't been migrated yet.
 */
const isValidAudioUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return Boolean(url.match(/\.(mp3|m4a|wav|ogg|webm|aac|flac)(\?|$)/i));
};

// ============================================================
// Audio Player Sub-Component
// ============================================================

interface AudioPlayerProps {
  src: string;
  disabled?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, disabled = false }) => {
  const theme = useTheme();
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [playCount, setPlayCount] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedData = () => {
      setAudioLoaded(true);
      setDuration(audio.duration);
      setAudioError(null);
    };
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => setIsPlaying(false);
    const handleError = () => {
      setAudioError("Failed to load audio. Please check your connection.");
      setAudioLoaded(false);
    };

    audio.addEventListener("loadeddata", handleLoadedData);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadeddata", handleLoadedData);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, []);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || !audioLoaded || disabled) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
      if (currentTime === 0) setPlayCount((prev) => prev + 1);
    }
  };

  const handleReplay = () => {
    const audio = audioRef.current;
    if (!audio || !audioLoaded || disabled) return;
    audio.currentTime = 0;
    setCurrentTime(0);
    audio.play();
    setIsPlaying(true);
    setPlayCount((prev) => prev + 1);
  };

  const handleVolumeToggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.volume = 1;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (audioError) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {audioError}
      </Alert>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        my: 2,
        backgroundColor: theme.palette.grey[50],
        borderRadius: 2,
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Headset sx={{ fontSize: 32, color: "primary.main" }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={600} color="primary.main">
            Audio Recording
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {audioLoaded ? `Duration: ${formatTime(duration)}` : "Loading..."}
            {playCount > 0 &&
              ` • Played: ${playCount} time${playCount > 1 ? "s" : ""}`}
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 3,
          mb: 2,
          backgroundColor: theme.palette.grey[300],
          "& .MuiLinearProgress-bar": { borderRadius: 3 },
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {formatTime(currentTime)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTime(duration)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
        <IconButton
          onClick={handleReplay}
          disabled={!audioLoaded || disabled}
          size="small"
          sx={{
            backgroundColor: theme.palette.grey[200],
            "&:hover": { backgroundColor: theme.palette.grey[300] },
          }}
        >
          <Replay />
        </IconButton>
        <IconButton
          onClick={handlePlayPause}
          disabled={!audioLoaded || disabled}
          size="large"
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            "&:hover": { backgroundColor: "primary.dark" },
            "&:disabled": { backgroundColor: theme.palette.grey[300] },
          }}
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>
        <IconButton
          onClick={handleVolumeToggle}
          disabled={!audioLoaded || disabled}
          size="small"
          sx={{
            backgroundColor: theme.palette.grey[200],
            "&:hover": { backgroundColor: theme.palette.grey[300] },
          }}
        >
          {isMuted ? <VolumeOff /> : <VolumeUp />}
        </IconButton>
      </Box>
    </Paper>
  );
};

// ============================================================
// Main Component
// ============================================================

const MultipleChoiceQuestionComponent: React.FC<
  MultipleChoiceQuestionProps
> = ({
  question,
  userAnswer,
  selectedAnswer,
  onAnswerChange,
  onComplete,
  disabled = false,
  showResult = false,
  reviewMode = false,
}) => {
  const theme = useTheme();
  const startTimeRef = useRef(Date.now());

  const subQuestionsArray = question.questions || question.subQuestions;

  const hasSubQuestions =
    subQuestionsArray &&
    Array.isArray(subQuestionsArray) &&
    subQuestionsArray.length > 0;
  const hasSimpleOptions =
    question.options &&
    Array.isArray(question.options) &&
    question.options.length > 0;

  // ============================================================
  // Media — simple convention:
  //   asset    = image  (.jpg, .png)
  //   audioUrl = audio  (.mp3, .m4a, .wav)
  // Both can exist simultaneously (e.g. listening_q4: map + audio)
  // ============================================================
  const hasImage = Boolean(question.asset);
  const hasAudio = isValidAudioUrl(question.audioUrl);

  // State for simple multiple choice
  const [currentAnswer, setCurrentAnswer] = useState<number | undefined>(
    typeof selectedAnswer === "number"
      ? selectedAnswer
      : typeof userAnswer === "number"
      ? userAnswer
      : undefined
  );

  // State for multi-sub-question format
  const [subAnswers, setSubAnswers] = useState<Record<number, number>>(() => {
    if (
      typeof userAnswer === "object" &&
      userAnswer !== null &&
      !Array.isArray(userAnswer)
    ) {
      return userAnswer;
    }
    if (
      typeof selectedAnswer === "object" &&
      selectedAnswer !== null &&
      !Array.isArray(selectedAnswer)
    ) {
      return selectedAnswer;
    }
    return {};
  });

  const [hasReadPassage, setHasReadPassage] = useState(false);
  const [vocabularyExpanded, setVocabularyExpanded] = useState(false);

  // Sync with props
  useEffect(() => {
    if (hasSubQuestions) {
      if (
        typeof userAnswer === "object" &&
        userAnswer !== null &&
        !Array.isArray(userAnswer)
      ) {
        setSubAnswers(userAnswer);
      }
    } else {
      if (typeof userAnswer === "number") {
        setCurrentAnswer(userAnswer);
      } else if (typeof selectedAnswer === "number") {
        setCurrentAnswer(selectedAnswer);
      }
    }
  }, [userAnswer, selectedAnswer, hasSubQuestions]);

  // ============================================================
  // Validation
  // ============================================================

  if (!question) {
    return (
      <Alert severity="error">
        Invalid question data: No question provided.
      </Alert>
    );
  }

  if (!hasSubQuestions && !hasSimpleOptions) {
    return (
      <Alert severity="error">
        Invalid question data: Multiple choice question must have either
        'options' array or 'questions'/'subQuestions' array with sub-questions.
      </Alert>
    );
  }

  // ============================================================
  // Handlers
  // ============================================================

  const handleSimpleAnswerChange = (selectedIndex: number) => {
    if (disabled || reviewMode) return;
    setCurrentAnswer(selectedIndex);
    if (onAnswerChange) onAnswerChange(selectedIndex);
  };

  const handleSubQuestionChange = (
    subQuestionIndex: number,
    optionIndex: number
  ) => {
    if (disabled || reviewMode) return;
    const newAnswers = { ...subAnswers, [subQuestionIndex]: optionIndex };
    setSubAnswers(newAnswers);
    if (onAnswerChange) onAnswerChange(newAnswers);
  };

  const getOptionLabel = (
    subQuestion: SubQuestion,
    optionIndex: number
  ): string => {
    const option = subQuestion.options[optionIndex];
    if (typeof option === "string") return option;
    return (option as SubQuestionOption).label;
  };

  const isOptionCorrect = (
    subQuestion: SubQuestion,
    optionIndex: number
  ): boolean => {
    const option = subQuestion.options[optionIndex];
    if (typeof option === "object" && option !== null) {
      if ("isTrue" in option && (option as SubQuestionOption).isTrue === true)
        return true;
      if (
        "isCorrect" in option &&
        (option as SubQuestionOption).isCorrect === true
      )
        return true;
    }
    if (typeof option === "string" && subQuestion.correctOption) {
      return option.trim() === subQuestion.correctOption.trim();
    }
    return false;
  };

  const getCorrectOptionIndex = (subQuestion: SubQuestion): number => {
    for (let i = 0; i < subQuestion.options.length; i++) {
      if (isOptionCorrect(subQuestion, i)) return i;
    }
    return -1;
  };

  const handleSubmit = () => {
    if (disabled || reviewMode) return;
    if (onComplete) {
      let isCorrect = false;
      if (hasSubQuestions) {
        let correctCount = 0;
        subQuestionsArray!.forEach((subQ, idx) => {
          const userSelectedIndex = subAnswers[idx];
          if (
            userSelectedIndex !== undefined &&
            isOptionCorrect(subQ, userSelectedIndex)
          )
            correctCount++;
        });
        isCorrect = correctCount === subQuestionsArray!.length;
      } else {
        isCorrect = currentAnswer === question.correctAnswer;
      }
      onComplete(isCorrect, Date.now() - startTimeRef.current);
    }
  };

  const handlePassageInteraction = () => {
    if (!hasReadPassage) setHasReadPassage(true);
  };

  // ============================================================
  // Render: Media Section
  // asset = image, audioUrl = audio — both rendered independently
  // ============================================================

  const renderMedia = () => {
    if (!hasImage && !hasAudio) return null;

    return (
      <Box sx={{ my: 2 }}>
        {/* Image from asset field */}
        {hasImage && (
          <Box sx={{ textAlign: "center", mb: hasAudio ? 2 : 0 }}>
            <CardMedia
              component="img"
              image={question.asset || ""}
              alt="Question reference"
              sx={{
                maxWidth: "100%",
                maxHeight: 400,
                objectFit: "contain",
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                mx: "auto",
              }}
            />
          </Box>
        )}

        {/* Audio player from audioUrl field */}
        {hasAudio && (
          <AudioPlayer src={question.audioUrl!} disabled={disabled} />
        )}
      </Box>
    );
  };

  // ============================================================
  // Render: Multi-Sub-Question Format
  // ============================================================

  if (hasSubQuestions) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {question.question}
          </Typography>
          {renderMedia()}
        </Paper>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {subQuestionsArray!.map((subQuestion, subIdx) => {
            const userSelectedIndex = subAnswers[subIdx];
            const isAnswered = userSelectedIndex !== undefined;
            const isUserCorrect =
              isAnswered && isOptionCorrect(subQuestion, userSelectedIndex);

            return (
              <Card
                key={subIdx}
                variant="outlined"
                sx={{
                  borderColor: reviewMode
                    ? isUserCorrect
                      ? theme.palette.success.main
                      : isAnswered
                      ? theme.palette.error.main
                      : theme.palette.divider
                    : theme.palette.divider,
                  borderWidth: reviewMode && isAnswered ? 2 : 1,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 500, flex: 1 }}
                    >
                      {subQuestion.question}
                    </Typography>
                    {reviewMode &&
                      isAnswered &&
                      (isUserCorrect ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Correct"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<Cancel />}
                          label="Incorrect"
                          color="error"
                          size="small"
                        />
                      ))}
                  </Box>

                  <RadioGroup
                    value={userSelectedIndex ?? ""}
                    onChange={(e) =>
                      handleSubQuestionChange(
                        subIdx,
                        parseInt(e.target.value, 10)
                      )
                    }
                  >
                    {subQuestion.options.map((option, optIdx) => {
                      const isSelected = userSelectedIndex === optIdx;
                      const isCorrectOpt = isOptionCorrect(subQuestion, optIdx);
                      const optionLabel = getOptionLabel(subQuestion, optIdx);

                      let backgroundColor = "transparent";
                      let borderColor = theme.palette.divider;

                      if (reviewMode) {
                        if (isCorrectOpt) {
                          backgroundColor = theme.palette.success.light + "30";
                          borderColor = theme.palette.success.main;
                        } else if (isSelected && !isCorrectOpt) {
                          backgroundColor = theme.palette.error.light + "20";
                          borderColor = theme.palette.error.main;
                        }
                      } else if (isSelected) {
                        backgroundColor = theme.palette.primary.light + "15";
                        borderColor = theme.palette.primary.main;
                      }

                      return (
                        <FormControlLabel
                          key={optIdx}
                          value={optIdx}
                          disabled={disabled || reviewMode}
                          control={
                            <Radio
                              color={
                                reviewMode && isCorrectOpt
                                  ? "success"
                                  : reviewMode && isSelected && !isCorrectOpt
                                  ? "error"
                                  : "primary"
                              }
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography>{optionLabel}</Typography>
                              {reviewMode && isCorrectOpt && (
                                <Chip
                                  label="Correct Answer"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          }
                          sx={{
                            mb: 1,
                            p: 1.5,
                            mx: 0,
                            borderRadius: 1,
                            border: `1px solid ${borderColor}`,
                            backgroundColor,
                            width: "100%",
                            "&:hover":
                              !disabled && !reviewMode
                                ? {
                                    backgroundColor: theme.palette.action.hover,
                                  }
                                : {},
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {!reviewMode && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {Object.keys(subAnswers).length} of {subQuestionsArray!.length}{" "}
              questions answered
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // ============================================================
  // Render: Simple Multiple Choice Format
  // ============================================================

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {question.question}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {question.passage
            ? "Read the passage below, then select the best answer."
            : "Select the best answer from the options below."}
        </Typography>
        {renderMedia()}
      </Paper>

      {question.passage && (
        <Card
          sx={{ mb: 3, cursor: "pointer" }}
          onClick={handlePassageInteraction}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <MenuBook color="primary" />
              <Typography variant="subtitle1" fontWeight="medium">
                Reading Passage
              </Typography>
              {hasReadPassage && (
                <Chip
                  icon={<CheckCircle />}
                  label="Read"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography
              variant="body1"
              sx={{ lineHeight: 1.8, whiteSpace: "pre-wrap" }}
              dangerouslySetInnerHTML={{ __html: question.passage }}
            />
          </CardContent>
        </Card>
      )}

      {question.vocabularyWords && question.vocabularyWords.length > 0 && (
        <Accordion
          expanded={vocabularyExpanded}
          onChange={() => setVocabularyExpanded(!vocabularyExpanded)}
          sx={{ mb: 3 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Spellcheck color="primary" />
              <Typography>
                Vocabulary ({question.vocabularyWords.length} words)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {question.vocabularyWords.map((vocab, idx) => (
                <Box key={idx} sx={{ display: "flex", gap: 1 }}>
                  <Typography fontWeight="medium">{vocab.word}:</Typography>
                  <Typography color="text.secondary">
                    {vocab.meaning}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Select your answer:
          </Typography>

          <RadioGroup
            value={currentAnswer ?? ""}
            onChange={(e) =>
              handleSimpleAnswerChange(parseInt(e.target.value, 10))
            }
          >
            {question.options!.map((option, index) => {
              const isSelected = currentAnswer === index;
              const isCorrectOption = question.correctAnswer === index;

              let backgroundColor = "transparent";
              let borderColor = theme.palette.divider;

              if (reviewMode || showResult) {
                if (isCorrectOption) {
                  backgroundColor = theme.palette.success.light + "30";
                  borderColor = theme.palette.success.main;
                } else if (isSelected && !isCorrectOption) {
                  backgroundColor = theme.palette.error.light + "20";
                  borderColor = theme.palette.error.main;
                }
              } else if (isSelected) {
                backgroundColor = theme.palette.primary.light + "15";
                borderColor = theme.palette.primary.main;
              }

              return (
                <FormControlLabel
                  key={index}
                  value={index}
                  disabled={disabled || reviewMode}
                  control={
                    <Radio
                      color={
                        (reviewMode || showResult) && isCorrectOption
                          ? "success"
                          : (reviewMode || showResult) &&
                            isSelected &&
                            !isCorrectOption
                          ? "error"
                          : "primary"
                      }
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography>{option}</Typography>
                      {(reviewMode || showResult) && isCorrectOption && (
                        <Chip
                          label="Correct Answer"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  sx={{
                    mb: 1,
                    p: 1.5,
                    mx: 0,
                    borderRadius: 1,
                    border: `1px solid ${borderColor}`,
                    backgroundColor,
                    width: "100%",
                    "&:hover":
                      !disabled && !reviewMode
                        ? { backgroundColor: theme.palette.action.hover }
                        : {},
                  }}
                />
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MultipleChoiceQuestionComponent;
